/**
 * No job left open — the blocked "On my way" screen (approved mockup A4,
 * https://claude.ai/artifact/TwMWRHEyVGpWnd2an3pVPv, Corbin 10/10).
 *
 * The way out is right on the block: one line names the open job, the three
 * big answers sit under it, and "Call the office" dials the company's one
 * office number (Settings → Company → phone). He never has to go find the
 * old job.
 *
 *   Done         → Review & Submit for that job (the submit gate takes it from there)
 *   Coming back  → the Coming back screen (why + when), same server answer
 *   Can't finish → the Can't finish screen (one reason per device), same server answer
 */
import { useEffect, useRef } from "react";
import { Ban, CalendarClock, Check, Lock, Phone, Truck, X } from "lucide-react";

import { formatPhone, jobTitle, type LeftOpenJob } from "@/lib/left-open";
import { cn } from "@/lib/utils";

export interface LeftOpenBlockProps {
    job: LeftOpenJob;
    /** The job he tried to head to, when the screen knows its name. */
    nextLabel?: string | null;
    officePhone: string | null;
    /** "I'm still here" is offered (on site at the open job, or GPS off). */
    canStayHere: boolean;
    onDone: () => void;
    onComingBack: () => void;
    onCantFinish: () => void;
    onStillHere: () => void;
    onClose: () => void;
}

export function LeftOpenBlock(p: LeftOpenBlockProps) {
    const first = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        first.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") p.onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const name = jobTitle(p.job);
    return (
        <div
            className="fixed inset-0 z-[80] flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="left-open-block-title"
            data-testid="left-open-block"
        >
            <div className="flex w-full max-w-3xl flex-col gap-4 overflow-y-auto bg-card p-5 text-foreground shadow-2xl sm:max-h-[94vh] sm:rounded-3xl sm:p-6">
                <div className="flex items-center gap-3 border-b border-border pb-3 text-base text-muted-foreground">
                    <Truck className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">
                        {p.nextLabel ? (
                            <>
                                Next: <b className="text-foreground">{p.nextLabel}</b>
                            </>
                        ) : (
                            "On my way"
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={p.onClose}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-secondary"
                        aria-label="Close"
                        data-testid="left-open-block-close"
                    >
                        <X className="h-6 w-6" aria-hidden />
                    </button>
                </div>

                <div className="flex items-center gap-4 rounded-2xl bg-amber-100 p-4 text-amber-950 dark:bg-amber-950/60 dark:text-amber-100">
                    <Lock className="h-12 w-12 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
                    <div className="min-w-0">
                        <p id="left-open-block-title" className="text-xl font-bold leading-tight sm:text-2xl">
                            {name} is still open.
                        </p>
                        <p className="text-base">Answer for it, then you can head out.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <AnswerButton
                        refEl={first}
                        tone="done"
                        icon={<Check className="h-11 w-11" aria-hidden />}
                        label="Done"
                        onClick={p.onDone}
                        testId="left-open-block-done"
                    />
                    <AnswerButton
                        tone="back"
                        icon={<CalendarClock className="h-11 w-11" aria-hidden />}
                        label="Coming back"
                        onClick={p.onComingBack}
                        testId="left-open-block-coming-back"
                    />
                    <AnswerButton
                        tone="cant"
                        icon={<Ban className="h-11 w-11" aria-hidden />}
                        label="Can't finish"
                        onClick={p.onCantFinish}
                        testId="left-open-block-cant-finish"
                    />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                    <p className="text-base text-muted-foreground">Not your job, or not sure?</p>
                    <div className="flex flex-wrap items-center gap-3">
                        {p.canStayHere && (
                            <button
                                type="button"
                                onClick={p.onStillHere}
                                className="min-h-[48px] rounded-xl border-2 border-border px-4 text-base font-bold hover:bg-secondary"
                                data-testid="left-open-block-still-here"
                            >
                                I&apos;m still here
                            </button>
                        )}
                        {p.officePhone && (
                            <a
                                href={`tel:${p.officePhone.replace(/[^\d+]/g, "")}`}
                                className="inline-flex min-h-[56px] items-center gap-2.5 rounded-xl border-2 border-border px-5 text-lg font-bold hover:bg-secondary"
                                data-testid="left-open-block-call"
                            >
                                <Phone className="h-6 w-6 text-emerald-700 dark:text-emerald-400" aria-hidden />
                                Call the office · {formatPhone(p.officePhone)}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AnswerButton(props: {
    tone: "done" | "back" | "cant";
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    testId: string;
    refEl?: React.Ref<HTMLButtonElement>;
}) {
    return (
        <button
            ref={props.refEl}
            type="button"
            onClick={props.onClick}
            data-testid={props.testId}
            className={cn(
                "flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl p-4 text-2xl font-bold text-white transition-colors",
                props.tone === "done" && "bg-emerald-700 hover:bg-emerald-800",
                props.tone === "back" && "bg-blue-700 hover:bg-blue-800",
                props.tone === "cant" && "bg-red-700 hover:bg-red-800",
            )}
        >
            {props.icon}
            {props.label}
        </button>
    );
}
